/**
 * Logger Utility
 * Production-safe logging that disables logs in production environment
 */

interface LoggerConfig {
    enabledInProduction: boolean;
    prefix: string;
}

const defaultConfig: LoggerConfig = {
    enabledInProduction: false,
    prefix: '[HYB]',
};

// Check if we're in production mode
const isProduction = process.env.NODE_ENV === 'production';

/**
 * Create a logger instance with optional configuration
 */
export function createLogger(name: string, config: Partial<LoggerConfig> = {}) {
    const mergedConfig = { ...defaultConfig, ...config };
    const prefix = `${mergedConfig.prefix} [${name}]`;

    const shouldLog = !isProduction || mergedConfig.enabledInProduction;

    return {
        debug: (...args: unknown[]) => {
            if (shouldLog) {
                console.debug(prefix, ...args);
            }
        },
        info: (...args: unknown[]) => {
            if (shouldLog) {
                console.info(prefix, ...args);
            }
        },
        warn: (...args: unknown[]) => {
            // Warnings always show (useful for debugging prod issues)
            console.warn(prefix, ...args);
        },
        error: (...args: unknown[]) => {
            // Errors always show
            console.error(prefix, ...args);
        },
        log: (...args: unknown[]) => {
            if (shouldLog) {
                console.log(prefix, ...args);
            }
        },
    };
}

// Pre-configured loggers for common use cases
export const logger = createLogger('App');
export const apiLogger = createLogger('API');
export const wsLogger = createLogger('WebSocket');
export const authLogger = createLogger('Auth');

/**
 * Log only in development mode
 * @deprecated Use createLogger instead for better organization
 */
export function devLog(...args: unknown[]): void {
    if (!isProduction) {
        console.log('[DEV]', ...args);
    }
}

/**
 * Always log errors regardless of environment
 */
export function logError(context: string, error: unknown): void {
    console.error(`[ERROR] [${context}]`, error);
}
